import React, { useContext, useMemo, useState } from "react";
import {
  Alert,
  Box,
  FormControl,
  Button,
  TextField,
  Grid,
  Typography,
} from "@mui/material";
import moment from "moment";
import { UserContext } from "../../../context/UserContext";
import AddressAutocomplete from "../../gmaps/AddressAutocomplete";
import { LocalizationProvider, StaticDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Option, { OPTION_GRID_TILE_SX } from "../../../product/component/Option";
import OptionGridCell from "../../../product/component/OptionGridCell";
import Price from "../Price";
import bkstApi from "../../../api";
import VariantSelectionCategory from "../../../product/component/VariantSelectionCategory";
import { capitalize, validateFulfillment } from "../../util";
import {
  getAvailableTimeslotsByDate,
  resolveStoreLocation,
  shouldDisableStoreDate,
} from "../../storeHours";

export default function FulfillmentTab(props) {
  const { user } = useContext(UserContext);
  const { fulfillment } = props;

  const [isAddressDone, setIsAddressDone] = useState(false);

  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  const storeLocations = user.config?.storeLocation;
  const hoursStore = useMemo(
    () => resolveStoreLocation(storeLocations, fulfillment),
    [storeLocations, fulfillment]
  );
  const fulfillmentMode =
    fulfillment?.type === "delivery" ? "delivery" : "pickup";
  const availableSlots = useMemo(() => {
    if (!fulfillment?.date || !hoursStore) return [];
    return getAvailableTimeslotsByDate(hoursStore, fulfillment.date, fulfillmentMode);
  }, [hoursStore, fulfillment?.date, fulfillmentMode]);

  const handleSelection = (val) => {
    if (val === "togo") {
      props.onChange({
        type: "togo",
        date: moment().format("YYYY-MM-DD"),
        time: moment().format("ha"),
        location: {
          ...user.config.storeLocation[0].address,
          id: user.config.storeLocation[0].id,
        },
        storeLocationId: user.config.storeLocation[0].id,
      });
    } else {
      props.onChange({ type: val, date: "", time: "", location: "" });
    }
  };

  const getDeliveryQuote = () => {
    const arr = validateFulfillment(fulfillment);
    setErrors(arr);
    if (arr.length > 0) {
      return;
    } else {
      setLoading(true);
      bkstApi
        .post(`/my/delivery/quote`, {
          address: fulfillment.location,
          provider: { name: "metrobi" },
        })
        .then((res) => {
          props.onChange({
            ...fulfillment,
            option: {
              delivery: { amount: res.data.quote, includeFees: false, total: res.data.quote },
            },
          });
          setLoading(false);
        })
        .catch((err) => {
          setErrors([err.response?.data?.msg]);
          setLoading(false);
        });
    }
  };

  return (
    <Box>
      <VariantSelectionCategory
        field={{ name: "Fulfillment", required: true }}
        cat={"Fulfillment"}
        variant={{ name: capitalize(fulfillment.type), value: fulfillment.type }}
        dontCollapseOnSelect={fulfillment.type === "togo"}
      >
        <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
          {[
            { type: "pickup", title: "Pickup" },
            { type: "delivery", title: "Delivery" },
            { type: "togo", title: "TO-GO" },
          ].map(({ type, title }) => (
            <OptionGridCell key={type} gridKey={`fulfillment-type-${type}`}>
              <Option
                title={title}
                sx={OPTION_GRID_TILE_SX}
                onClick={() => handleSelection(type)}
                selected={fulfillment.type === type}
              />
            </OptionGridCell>
          ))}
        </Grid>
      </VariantSelectionCategory>
      {fulfillment?.type === "pickup" && (
        <VariantSelectionCategory
          field={{ name: "Pickup Location", required: true }}
          cat={"Pickup Location"}
          variant={{ name: fulfillment?.location.street, value: fulfillment?.location }}
        >
          <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
            {user.config.storeLocation.map((o, idx) => (
              <OptionGridCell
                key={o.address.street ?? idx}
                gridKey={`pickup-location-${idx}`}
              >
                <Option
                  title={o.address.street}
                  description={`${o.address.city}, ${o.address.state}`}
                  sx={OPTION_GRID_TILE_SX}
                  onClick={() =>
                    props.onChange({
                      ...fulfillment,
                      location: { ...o.address, id: o.id },
                      storeLocationId: o.id,
                      date: "",
                      time: "",
                    })
                  }
                  selected={
                    fulfillment?.location?.id === o.id ||
                    fulfillment?.storeLocationId === o.id ||
                    (!fulfillment?.location?.id &&
                      !fulfillment?.storeLocationId &&
                      fulfillment?.location?.street === o.address.street)
                  }
                />
              </OptionGridCell>
            ))}
          </Grid>
        </VariantSelectionCategory>
      )}
      {fulfillment?.type === "delivery" && (
        <VariantSelectionCategory
          field={{ name: `${fulfillment.type} Details`, required: true }}
          cat={`${fulfillment.type} Details`}
          variant={{
            name: fulfillment.location.street,
            value: isAddressDone ? fulfillment.location : "",
          }}
        >
          <Box>
            <Box>
              <FormControl variant="outlined" fullWidth>
                <AddressAutocomplete
                  value={fulfillment?.location || ""}
                  onPlaceSelect={(loc) => {
                    const o = { ...fulfillment, location: loc };
                    delete o.storeLocationId;
                    props.onChange(o);
                  }}
                  label="Delivery Address"
                  required
                />
              </FormControl>
            </Box>
            <Box my={2}>
              <TextField
                id="instructions"
                label="Instructions (optional)"
                name="instructions"
                variant="outlined"
                fullWidth
                value={fulfillment?.instructions || ""}
                onChange={(e) => props.onChange({ ...fulfillment, instructions: e.target.value })}
                helperText="Instructions for delivery driver"
              />
            </Box>
            <Box textAlign={"right"}>
              <Button color="primary" variant="contained" onClick={() => setIsAddressDone(true)}>
                Done
              </Button>
            </Box>
          </Box>
        </VariantSelectionCategory>
      )}
      {(fulfillment.type === "pickup" || fulfillment.type === "delivery") && (
        <VariantSelectionCategory
          field={{ name: `${fulfillment.type} Date`, required: true }}
          cat={`${fulfillment.type} Date`}
          variant={{
            name: fulfillment.date ? moment(fulfillment.date).format("ddd, MMM DD, YYYY") : "",
            value: fulfillment.date,
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <StaticDatePicker
              sx={{
                border: "1px solid #ECECEC",
                width: "100%",
                "& .MuiDateCalendar-root": {
                  width: "100%",
                  maxWidth: "100%",
                  height: "auto",
                  maxHeight: "none",
                },
                "& .MuiDayCalendar-slideTransition": {
                  minHeight: 340,
                  height: "auto",
                  overflow: "visible",
                },
                "& .MuiDayCalendar-header, & .MuiDayCalendar-weekContainer": {
                  width: "100%",
                  justifyContent: "space-around",
                },
                "& .MuiDayCalendar-weekDayLabel": {
                  width: 48,
                  height: 40,
                  fontSize: "0.95rem",
                  margin: 0,
                },
                "& .MuiPickersDay-root": {
                  width: 48,
                  height: 48,
                  fontSize: "1.15rem",
                  margin: "2px",
                },
              }}
              slotProps={{
                actionBar: {
                  actions: [],
                },
                toolbar: { hidden: true },
              }}
              views={["day"]}
              disablePast
              shouldDisableDate={(d) =>
                shouldDisableStoreDate(hoursStore, d, fulfillmentMode)
              }
              value={fulfillment.date ? moment(fulfillment.date).toDate() : null}
              format="MMM d, y"
              onChange={(value) => {
                props.onChange({
                  ...fulfillment,
                  date: (value && moment(value).format("YYYY-MM-DD")) || "",
                  time: "",
                });
              }}
            />
          </LocalizationProvider>
        </VariantSelectionCategory>
      )}
      {(fulfillment.type === "pickup" || fulfillment.type === "delivery") &&
        fulfillment.date && (
        <VariantSelectionCategory
          field={{ name: `${fulfillment.type} Time`, required: true }}
          cat={`${fulfillment.type} Time`}
          variant={{ name: fulfillment.time, value: fulfillment.time }}
        >
          {availableSlots.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No available timeslots for this date
            </Typography>
          ) : (
            <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
              {availableSlots.map((slot, idx) => (
                <OptionGridCell key={slot} gridKey={`fulfillment-time-${idx}`}>
                  <Option
                    title={slot}
                    sx={OPTION_GRID_TILE_SX}
                    onClick={() => props.onChange({ ...fulfillment, time: slot })}
                    selected={fulfillment.time === slot}
                  />
                </OptionGridCell>
              ))}
            </Grid>
          )}
        </VariantSelectionCategory>
      )}
      {fulfillment?.type === "delivery" && (
        <VariantSelectionCategory
          field={{ name: `${fulfillment.type} Fee`, required: true }}
          cat={`${fulfillment.type} Fee`}
          variant={{ name: fulfillment.provider?.name, value: fulfillment.provider }}
        >
          {/* <Tabs value={fulfillment?.provider?.name || "in-house"}>
            <Tab
              value={"in-house"}
              label={"In-House Delivery"}
              onClick={() => {
                props.onChange({ ...fulfillment, provider: "" });
              }}
            />
            <Tab
              value="metrobi"
              label={"Metrobi"}
              onClick={() => {
                props.onChange({ ...fulfillment, provider: { name: "metrobi" } });
              }}
            />
          </Tabs>*/}
          {fulfillment.provider?.name !== "metrobi" && (
            <Box>
              <Price
                label={"Delivery fee"}
                value={
                  fulfillment.option?.delivery || {
                    amount: "",
                    includeFees: user.config?.transferFees === "false" ? false : true,
                  }
                }
                disableFees={true}
                onChange={(o) => {
                  props.onChange({ ...fulfillment, option: { delivery: o } });
                }}
              />
            </Box>
          )}
          {fulfillment.provider?.name === "metrobi" && (
            <Box mt={4}>
              <Button color={"primary"} variant="outlined" onClick={getDeliveryQuote}>
                Get Delivery Quote
              </Button>
              <Box>
                {errors.length > 0 && (
                  <Alert sx={{ justifyContent: "center" }} severity="error">
                    {errors.join(". ")}
                  </Alert>
                )}
              </Box>
            </Box>
          )}
        </VariantSelectionCategory>
      )}
      <Box my={2}>
        <Button color="primary" variant="contained" fullWidth onClick={props.onSubmit}>
          CONTINUE
        </Button>
      </Box>
    </Box>
  );
}
